// Plik: src/components/HighTicketClient.tsx
"use client";

import Link from "next/link";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// --- KOMPONENTY POMOCNICZE (IKONY, GRIDY) ---

const BorderBeam = ({ duration = 10, delay = 0 }) => {
  return (
    <div className="absolute inset-0 rounded-xl pointer-events-none overflow-hidden">
        <motion.div
            className="absolute -inset-[100%]"
            animate={{ rotate: [0, 360] }}
            transition={{ duration: duration, repeat: Infinity, ease: "linear", delay: delay }}
            style={{
                background: "conic-gradient(from 0deg at 50% 50%, rgba(255,255,255,1) 0deg, rgba(255,255,255,0.3) 180deg, rgba(255,255,255,1) 360deg)",
                filter: "blur(8px)",
            }}
        />
        <div className="absolute inset-[1px] bg-inherit rounded-xl z-10" />
    </div>
  );
};

const GridPattern = () => (
  <div className="absolute inset-0 w-full h-full opacity-[0.04] pointer-events-none" 
       style={{ 
         backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)', 
         backgroundSize: '40px 40px' 
       }}>
  </div>
);

const GridPatternDark = () => (
  <div className="absolute inset-0 w-full h-full opacity-[0.03] pointer-events-none" 
       style={{ 
         backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', 
         backgroundSize: '40px 40px' 
       }}>
  </div>
);

// ICONS
const PhoneIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
);

const NetworkIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line></svg>
);

const CheckIcon = () => (
  <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600">
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>
  </div>
);

const XIcon = () => (
  <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-500">
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M18 6L6 18M6 6l12 12"/></svg>
  </div>
);

const TerminalIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="4 17 10 11 4 5"></polyline><line x1="12" y1="19" x2="20" y2="19"></line></svg>
);

// Ikona Plusa/Minusa do FAQ
const PlusIcon = ({ isOpen }: { isOpen: boolean }) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform duration-300 ${isOpen ? "rotate-45" : ""}`}>
        <line x1="12" y1="5" x2="12" y2="19"></line>
        <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
);

// --- KOMPONENT FAQ ITEM ---
const FaqItem = ({ question, answer, isOpen, onClick }: { question: string, answer: string, isOpen: boolean, onClick: () => void }) => {
    return (
        <div className="border-b border-gray-200 last:border-0">
            <button onClick={onClick} className="w-full py-6 flex items-center justify-between text-left focus:outline-none group">
                <span className={`text-lg lg:text-xl font-bold transition-colors duration-300 pr-8 ${isOpen ? "text-blue-600" : "text-gray-900 group-hover:text-blue-600"}`}>{question}</span>
                <span className={`flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all duration-300 ${isOpen ? "bg-blue-600 border-blue-600 text-white rotate-45" : "bg-white border-gray-200 text-gray-500 group-hover:border-blue-600 group-hover:text-blue-600"}`}>
                    <PlusIcon isOpen={isOpen} />
                </span>
            </button>
            <AnimatePresence>
                {isOpen && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} className="overflow-hidden">
                        <div className="pb-8 text-gray-800 leading-relaxed text-lg font-medium max-w-3xl">{answer}</div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default function HighTicketClient() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const toggleFaq = (index: number) => setOpenFaqIndex(openFaqIndex === index ? null : index);

  const faqData = [
    { 
        q: "Czy Cold Calling jeszcze działa?", 
        a: "Działa lepiej niż kiedykolwiek, jeśli robi się to dobrze. Większość ludzi boi się dzwonić, dlatego telefon decydenta jest mniej zatłoczony niż jego skrzynka mailowa. Moja 'gadka' nie polega na czytaniu skryptu, ale na budowaniu relacji od pierwszego 'Halo'." 
    },
    { 
        q: "Jakie branże obsługujesz?", 
        a: "Specjalizuję się w sprzedaży High Ticket B2B. Usługi IT, consulting, OZE, nieruchomości, marketing – wszędzie tam, gdzie wartość klienta (LTV) jest wysoka, a proces decyzyjny wymaga zaufania i rozmowy." 
    },
    { 
        q: "Na czym polegają Twoje metody na Facebook/Google?", 
        a: "To autorski research. Nie korzystam tylko z gotowych baz. Potrafię znaleźć bezpośredni kontakt do osób, które nie są dostępne publicznie, wykorzystując zaawansowane operatory wyszukiwania i analizę grup na Facebooku." 
    },
    { 
        q: "Co to znaczy, że udostępniasz swój Network?", 
        a: "Przez lata zbudowałem sieć kontaktów biznesowych. Jeśli Twój produkt pasuje do potrzeb moich znajomych z branży, rekomenduję go bezpośrednio. To najskuteczniejsza forma sprzedaży – przez polecenie." 
    }
  ];

  return (
    <main className="bg-[#F7F7F8] w-full min-h-screen relative selection:bg-blue-200 font-sans text-gray-900">
      

      {/* --- HERO SECTION --- */}
      <section className="relative pt-32 pb-12 lg:pt-48 lg:pb-12 px-6 overflow-hidden">
        <GridPattern />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80vw] h-[50vh] bg-blue-500/5 blur-[120px] pointer-events-none rounded-full"></div>
        <div className="mx-auto max-w-[1250px] relative z-10">
            <div className="max-w-4xl mr-auto text-left">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-2 rounded-full bg-white border border-gray-200 px-3 py-1 text-xs font-bold text-blue-600 mb-6 shadow-sm mx-0">
                    <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span></span>
                    HIGH TICKET PROSPECTING
                </motion.div>
                
                <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-5xl lg:text-7xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-8">
                    Sprzedaż to <br/>
                    <span className="text-blue-600">sztuka rozmowy.</span>
                </motion.h1>
                
                <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-xl text-gray-600 leading-relaxed max-w-2xl mx-0 font-medium">
                    Zapomnij o sztywnych skryptach. Oferuję <strong>Cold Calling, Cold Emailing i Networking</strong> na najwyższym poziomie. Docieram tam, gdzie inni boją się zadzwonić.
                </motion.p>
                
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-10 flex flex-wrap gap-4 justify-start">
                    <Link href="/kontakt" className="rounded-full bg-black px-8 py-4 text-sm font-bold text-white transition hover:bg-gray-800 shadow-xl hover:shadow-2xl hover:-translate-y-1">
                        Umów rozmowę
                    </Link>
                    <Link href="#metody" className="rounded-full bg-white border border-gray-200 px-8 py-4 text-sm font-bold text-gray-900 transition hover:bg-gray-50 hover:border-gray-300">
                        Moje metody
                    </Link>
                </motion.div>
            </div>
        </div>
      </section>

      {/* --- PORÓWNANIE (PROBLEM VS ROZWIĄZANIE) --- */}
      <section className="pt-10 pb-20 lg:pt-0 lg:pb-32 px-6 relative">
        <GridPattern />
        <div className="mx-auto max-w-[1250px] relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start lg:-mt-[10px]">
                
                {/* KARTA 1: PROBLEM (Natrętny Telemarketer) */}
                <div className="relative rounded-3xl bg-white border border-gray-200 p-8 lg:p-12 shadow-sm transition hover:shadow-md">
                    <div className="mb-8">
                        <div className="inline-block p-3 rounded-xl bg-red-50 text-red-500 mb-4">
                            {/* ZMIANA: IKONA OSTRZEŻENIA (Wykrzyknik w trójkącie) */}
                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" className="bi bi-exclamation-triangle" viewBox="0 0 16 16">
                                <path d="M7.938 2.016A.13.13 0 0 1 8.002 2a.13.13 0 0 1 .063.016.15.15 0 0 1 .054.057l6.857 11.667c.036.06.035.124.002.183a.2.2 0 0 1-.054.06.1.1 0 0 1-.066.017H1.146a.1.1 0 0 1-.066-.017.2.2 0 0 1-.054-.06.18.18 0 0 1 .002-.183L7.884 2.073a.15.15 0 0 1 .054-.057m1.044-.45a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767z"/>
                                <path d="M7.002 12a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 5.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z"/>
                            </svg>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">Irytujący Akwizytor</h3>
                        <p className="text-gray-500">Większość handlowców czyta z kartki i brzmi jak robot. Ludzie wyczuwają to w sekundę i rozłączają się z irytacją.</p>
                    </div>
                    <ul className="space-y-4">
                        <li className="flex items-start gap-3 text-gray-600">
                            <XIcon /> <span>Sztywny skrypt i brak luzu</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-600">
                            <XIcon /> <span>Nachalna sprzedaż od 1. zdania</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-600">
                            <XIcon /> <span>Brak personalizacji ("Dzwonię z firmy X")</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-600">
                            <XIcon /> <span>Spalone kontakty przez amatorskie podejście</span>
                        </li>
                    </ul>
                </div>

                {/* KARTA 2: ROZWIĄZANIE (Twój Styl) */}
                <div className="relative rounded-3xl bg-white border border-blue-200 p-8 lg:p-12 shadow-2xl shadow-blue-900/5 ring-4 ring-blue-50/50">
                    <div className="absolute top-0 right-0 bg-blue-600 text-white text-[10px] font-bold px-3 py-1.5 rounded-bl-xl rounded-tr-2xl uppercase tracking-wider">
                        Twój wybór
                    </div>
                    <div className="mb-8">
                        <div className="inline-block p-3 rounded-xl bg-blue-50 text-blue-600 mb-4">
                            {/* ZMIANA: IKONA EMOJI (Uśmiech) */}
                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" className="bi bi-emoji-smile" viewBox="0 0 16 16">
                                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
                                <path d="M4.285 9.567a.5.5 0 0 1 .683.183A3.5 3.5 0 0 0 8 11.5a3.5 3.5 0 0 0 3.032-1.75.5.5 0 1 1 .866.5A4.5 4.5 0 0 1 8 12.5a4.5 4.5 0 0 1-3.898-2.25.5.5 0 0 1 .183-.683M7 6.5C7 7.328 6.552 8 6 8s-1-.672-1-1.5S5.448 5 6 5s1 .672 1 1.5m4 0c0 .828-.448 1.5-1 1.5s-1-.672-1-1.5S9.448 5 10 5s1 .672 1 1.5"/>
                            </svg>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">Mój Styl: Relacja</h3>
                        <p className="text-gray-500">Potrafię sprawić, że ludzie otwierają się przede mną przez telefon, nawet jeśli nie wiedzą, kim jestem. To czysta psychologia i luz.</p>
                    </div>
                    <ul className="space-y-4">
                        <li className="flex items-start gap-3 text-gray-800 font-medium">
                            <CheckIcon /> <span>Naturalna rozmowa, zero robota</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-800 font-medium">
                            <CheckIcon /> <span>Klienci podpisują umowy zdalnie</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-800 font-medium">
                            <CheckIcon /> <span>Budowanie zaufania bez wizytówki</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-800 font-medium">
                            <CheckIcon /> <span>Skuteczność oparta na osobowości</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-800 font-medium">
                            <CheckIcon /> <span>Wysokie zamknięcia sprzedaży (High Ticket)</span>
                        </li>
                    </ul>
                </div>

            </div>
        </div>
      </section>

      {/* --- MOJE METODY (CIEMNA, FULL SCREEN DESKTOP) --- */}
      <section id="metody" className="bg-[#0b0c0e] py-16 lg:py-0 lg:min-h-screen flex flex-col justify-center relative overflow-hidden z-30 border-t border-white/5">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60%] h-[60%] bg-blue-900/10 blur-[150px] pointer-events-none"></div>
        <div className="mx-auto max-w-[1250px] px-6 relative z-10 w-full">
          
          <div className="text-left lg:text-center max-w-3xl mx-0 lg:mx-auto mb-16">
             <h2 className="text-3xl lg:text-6xl font-bold tracking-tight text-white mb-6">
               Trzy filary <br />
               <span className="text-gray-400">skuteczności.</span>
             </h2>
             <p className="text-sm lg:text-lg text-gray-500 max-w-2xl mx-0 lg:mx-auto">
               Nie polegam na szczęściu. Opieram się na sprawdzonych metodach, własnym networku i umiejętności budowania relacji od pierwszego kontaktu.
             </p>
          </div>

          <div className="relative w-full max-w-4xl mx-auto mb-16 lg:mb-24">
             <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[110%] h-[110%] bg-gradient-to-r from-blue-600/20 to-purple-500/20 blur-[80px] rounded-full -z-10 opacity-60"></div>
             
             {/* CENTRALNY ELEMENT - TELEFON/ROZMOWA */}
             <div className="relative aspect-[16/9] lg:aspect-[21/9] w-full rounded-2xl overflow-hidden shadow-2xl bg-[#13151a] border border-white/5">
                 <BorderBeam duration={10} delay={0} />
                 <div className="absolute inset-[1px] bg-[#0b0c0e] rounded-2xl flex items-center justify-center overflow-hidden z-10">
                    <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20"></div>
                    <div className="text-center p-8">
                       <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6 border border-white/10 relative">
                          <div className="absolute inset-0 bg-green-500/20 rounded-full animate-ping opacity-50"></div>
                          <PhoneIcon />
                       </div>
                       <p className="text-white text-xl lg:text-2xl font-bold mb-2">„Dzień dobry, czy rozmawiam z...”</p>
                       <p className="text-gray-500 text-xs lg:text-sm uppercase tracking-widest font-bold">Cold Calling Mastery</p>
                    </div>
                 </div>
             </div>
          </div>

          {/* TRZY KOLUMNY CECH */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 text-left max-w-5xl mx-auto">
             
             {/* Kolumna 1: Cold Calling */}
             <div className="space-y-4">
                <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center text-green-500 border border-green-500/20">
                   <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" /></svg>
                </div>
                <h3 className="text-xl font-bold text-white">Mistrzowska Gadka</h3>
                <p className="text-gray-500 leading-relaxed text-sm">Nie jestem telemarketerem z kartki. Potrafię rozmawiać, przełamywać lody i budować zaufanie przez telefon. To moja supermoc.</p>
             </div>

             {/* Kolumna 2: Networking */}
             <div className="space-y-4">
                <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-500 border border-blue-500/20">
                   <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" /></svg>
                </div>
                <h3 className="text-xl font-bold text-white">Szeroki Network</h3>
                <p className="text-gray-500 leading-relaxed text-sm">Przez lata zbudowałem bazę kontaktów. Wiem, kto jest kim w branży. Często jeden telefon do znajomego zastępuje tygodnie dobijania się.</p>
             </div>

             {/* Kolumna 3: FB/Google Research */}
             <div className="space-y-4">
                <div className="w-12 h-12 rounded-lg bg-purple-500/10 flex items-center justify-center text-purple-500 border border-purple-500/20">
                   <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" /></svg>
                </div>
                <h3 className="text-xl font-bold text-white">Smart Research</h3>
                <p className="text-gray-500 leading-relaxed text-sm">Mam swoje sposoby na Facebooku i w Google. Znajduję decydentów tam, gdzie inni nie potrafią. Wiem, jak dotrzeć bezpośrednio.</p>
             </div>

          </div>
        </div>
      </section>

      {/* --- DEEP DIVE: MÓJ NETWORK (JASNA, Sticky, NAPRAWIONA WIZUALIZACJA) --- */}
      <section className="bg-[#F7F7F8] py-16 lg:py-24 relative overflow-hidden border-t border-gray-200">
        <div className="mx-auto grid max-w-[1200px] grid-cols-1 gap-12 lg:gap-16 px-6 lg:grid-cols-[1fr_400px] items-start">
            
            {/* LEWA KOLUMNA (Treść) */}
            <div className="flex flex-col gap-8">
                <div className="relative">
                    <div className="inline-flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700 mb-4">
                        <NetworkIcon /> SIŁA RELACJI
                    </div>
                    <h2 className="text-4xl font-bold tracking-tight text-gray-900 md:text-5xl leading-[1.15] mb-6">
                        Ludzie kupują od <br />
                        <span className="text-blue-600">ludzi, których lubią.</span>
                    </h2>
                    <p className="text-lg text-gray-600 leading-relaxed">
                        W High Ticket nie sprzedaje się funkcjami, tylko zaufaniem. Moim zadaniem jest otworzyć drzwi, które dla innych są zamknięte.
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-4">
                    <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center text-green-600 mb-4 font-bold text-xl">🤝</div>
                        <h4 className="font-bold text-gray-900 mb-2">Relacje Osobiste</h4>
                        <p className="text-sm text-gray-500">Wykorzystuję prywatny network, by skracać dystans do decydentów.</p>
                    </div>
                    <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600 mb-4 font-bold text-xl">🕵️</div>
                        <h4 className="font-bold text-gray-900 mb-2">OSINT & Research</h4>
                        <p className="text-sm text-gray-500">Wyszukuję informacje na FB i w Google, o których konkurencja nie ma pojęcia.</p>
                    </div>
                    <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center text-orange-600 mb-4 font-bold text-xl">🗣️</div>
                        <h4 className="font-bold text-gray-900 mb-2">Perswazja</h4>
                        <p className="text-sm text-gray-500">Jestem dobry w gadce. Potrafię zamienić "nie jestem zainteresowany" w "proszę o ofertę".</p>
                    </div>
                    <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mb-4 font-bold text-xl">🚀</div>
                        <h4 className="font-bold text-gray-900 mb-2">Cold Emailing</h4>
                        <p className="text-sm text-gray-500">Piszę maile, które są otwierane i czytane, a nie usuwane.</p>
                    </div>
                </div>
            </div>

            {/* PRAWA KOLUMNA (Network Visual) */}
            <div className="relative mt-8 lg:mt-0 hidden lg:block">
                <div className="sticky top-32 rounded-xl bg-white p-8 shadow-[0_20px_50px_rgba(0,0,0,0.1)] border border-gray-200 h-[400px] relative overflow-hidden">
                    <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '20px 20px' }}></div>
                    <svg className="absolute inset-0 w-full h-full pointer-events-none z-10">
                        <line x1="50%" y1="50%" x2="20%" y2="25%" stroke="#e5e7eb" strokeWidth="2" />
                        <line x1="50%" y1="50%" x2="80%" y2="20%" stroke="#e5e7eb" strokeWidth="2" />
                        <line x1="50%" y1="50%" x2="85%" y2="60%" stroke="#e5e7eb" strokeWidth="2" />
                        <line x1="50%" y1="50%" x2="30%" y2="75%" stroke="#e5e7eb" strokeWidth="2" />
                        <line x1="50%" y1="50%" x2="65%" y2="85%" stroke="#e5e7eb" strokeWidth="2" />
                    </svg>
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30">
                        <div className="absolute -inset-4 bg-blue-500/20 rounded-full animate-pulse z-0"></div>
                        <div className="relative z-10 w-24 h-24 rounded-full bg-black text-white flex items-center justify-center font-bold text-xl shadow-2xl border-4 border-white ring-1 ring-gray-100">JA</div>
                    </div>
                    <div className="absolute top-[25%] left-[20%] -translate-x-1/2 -translate-y-1/2 z-20 w-16 h-16 rounded-full bg-blue-50 border border-blue-100 text-blue-600 flex flex-col items-center justify-center font-bold text-xs shadow-md"><span className="text-[10px] font-normal text-blue-400 mb-0.5">Tech</span>CEO</div>
                    <div className="absolute top-[20%] left-[80%] -translate-x-1/2 -translate-y-1/2 z-20 w-14 h-14 rounded-full bg-purple-50 border border-purple-100 text-purple-600 flex flex-col items-center justify-center font-bold text-xs shadow-md"><span className="text-[10px] font-normal text-purple-400 mb-0.5">Fin</span>VC</div>
                    <div className="absolute top-[60%] left-[85%] -translate-x-1/2 -translate-y-1/2 z-20 w-16 h-16 rounded-full bg-green-50 border border-green-100 text-green-600 flex flex-col items-center justify-center font-bold text-xs shadow-md"><span className="text-[10px] font-normal text-green-400 mb-0.5">Sales</span>Dyr.</div>
                    <div className="absolute top-[75%] left-[30%] -translate-x-1/2 -translate-y-1/2 z-20 w-14 h-14 rounded-full bg-orange-50 border border-orange-100 text-orange-600 flex flex-col items-center justify-center font-bold text-xs shadow-md"><span className="text-[10px] font-normal text-orange-400 mb-0.5">Mktg</span>CMO</div>
                    <div className="absolute top-[85%] left-[65%] -translate-x-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-gray-50 border border-gray-100 text-gray-600 flex flex-col items-center justify-center font-bold text-xs shadow-md">Inwestor</div>
                </div>
            </div>
        </div>
      </section>

      {/* --- NOWOŚĆ: FILOZOFIA SPRZEDAŻY (SEKCJA DODANA) --- */}
      <section className="bg-white py-20 lg:py-32 px-6 border-t border-gray-200">
        <div className="mx-auto max-w-[1250px]">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
                
                {/* LEWA KOLUMNA: TREŚĆ */}
                <div>
                    <span className="text-blue-600 font-bold tracking-widest uppercase text-xs mb-4 block">Filozofia Sprzedaży</span>
                    <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6">Relacja ponad <br /><span className="text-blue-600">algorytm.</span></h2>
                    <div className="prose prose-lg text-gray-600 leading-relaxed">
                        <p>
                            W świecie, gdzie każdy używa botów do spamowania skrzynek, <strong>bycie człowiekiem</strong> stało się supermocą. 
                        </p>
                        <p className="mt-4">
                             Wierzę w "Snajperski Prospekting". Zamiast strzelać na oślep do 1000 firm z nadzieją, że ktoś odpisze, wybieramy 50 idealnych klientów.
                        </p>
                        <p className="mt-4 font-medium text-gray-900">
                            Docieramy do nich tak skutecznie i z takim przygotowaniem, że po prostu nie mogą Cię zignorować. To nie magia, to szacunek do czasu klienta.
                        </p>
                    </div>
                </div>

                {/* PRAWA KOLUMNA: WIZUALIZACJA (CHAOS vs STRUKTURA) */}
                <div className="relative">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        
                        {/* Karta 1: CHAOS (SPAM) */}
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 opacity-60 scale-95 blur-[1px]">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="w-2 h-2 rounded-full bg-red-400"></div>
                                <span className="text-xs font-bold text-red-400 uppercase">Masowy Mailing</span>
                            </div>
                            <div className="space-y-3">
                                <div className="flex items-center justify-between text-xs text-gray-400">
                                    <span>Wysłano: 1000</span>
                                    <span>Odp: 0</span>
                                </div>
                                <div className="h-2 w-full bg-red-100 rounded overflow-hidden">
                                    <div className="h-full w-[2%] bg-red-400"></div>
                                </div>
                                <div className="flex gap-1 mt-2">
                                     <div className="h-8 w-full bg-gray-200 rounded animate-pulse"></div>
                                     <div className="h-8 w-full bg-gray-200 rounded animate-pulse"></div>
                                </div>
                            </div>
                        </div>

                        {/* Karta 2: PRECYZJA (WEBPOL) */}
                        <div className="bg-white border border-blue-100 rounded-xl p-6 shadow-xl shadow-blue-100 relative md:-ml-12 md:mt-12 ring-1 ring-blue-50">
                            <div className="absolute top-0 right-0 bg-blue-600 text-white text-[10px] font-bold px-2 py-1 rounded-bl-lg rounded-tr-lg">SKUTECZNOŚĆ</div>
                            <div className="flex items-center gap-2 mb-6">
                                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                <span className="text-xs font-bold text-gray-900 uppercase">Direct Approach</span>
                            </div>
                            <div className="space-y-4">
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-bold text-xs">CEO</div>
                                    <div>
                                        <div className="h-2 w-24 bg-gray-800 rounded mb-1"></div>
                                        <div className="text-[10px] text-green-600 font-bold bg-green-50 px-2 py-0.5 rounded-full inline-block">Meeting Booked</div>
                                    </div>
                                </div>
                                <div className="h-10 w-full bg-gray-50 rounded border border-gray-100 flex items-center justify-center gap-2 text-gray-500 text-xs">
                                     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                                     Poniedziałek, 10:00
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

            </div>
        </div>
      </section>

      {/* --- PROCES WSPÓŁPRACY (CIEMNA, FULL SCREEN DESKTOP) --- */}
      <section id="proces" className="py-24 px-6 relative overflow-hidden bg-[#0b0c0e] border-t border-white/5 lg:py-0 lg:min-h-screen flex flex-col justify-center">
        <GridPatternDark />
        <div className="mx-auto max-w-[1000px] relative z-10 w-full">
            <div className="mb-16 text-left lg:text-center">
                <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6">Jak to robimy?</h2>
                <p className="text-xl text-gray-400 max-w-2xl mx-0 lg:mx-auto">Konkretnie i bez owijania w bawełnę. Mój proces, Twoje wyniki.</p>
            </div>

            <div className="relative">
                <div className="absolute left-4 lg:left-1/2 top-0 bottom-0 w-px bg-white/10 -z-10"></div>
                <div className="space-y-12 lg:space-y-0">
                    
                    {/* ETAP 1 */}
                    <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between group lg:mb-12">
                        <div className="w-full pl-12 lg:w-[45%] lg:pl-0 lg:pr-12 lg:text-right order-2 lg:order-1 mt-2 lg:mt-0">
                            <span className="inline-block px-2 py-1 bg-blue-500/10 text-blue-400 text-[10px] font-bold uppercase tracking-wider rounded mb-2">Etap 01</span>
                            <h3 className="text-xl font-bold text-white mb-2">Głęboki Research</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">Używam moich metod na Facebooku i w Google, żeby znaleźć decydentów. Buduję listę, której nie ma Twoja konkurencja.</p>
                        </div>
                        <div className="absolute left-4 lg:left-1/2 w-8 h-8 lg:w-10 lg:h-10 -translate-x-1/2 bg-[#0b0c0e] border-4 border-blue-500 rounded-full flex items-center justify-center z-10 shadow-sm order-1">
                            <span className="text-[10px] lg:text-xs font-bold text-blue-500">1</span>
                        </div>
                        <div className="w-full pl-12 lg:w-[45%] lg:pl-12 order-3 mt-4 lg:mt-0">
                             <ul className="text-xs text-gray-400 space-y-1 bg-white/5 p-4 rounded-xl border border-white/5 inline-block w-full">
                                <li className="flex items-center gap-2">🔹 Analiza Facebook/Google</li>
                                <li className="flex items-center gap-2">🔹 Weryfikacja kontaktów</li>
                                <li className="flex items-center gap-2">🔹 Określenie celu</li>
                             </ul>
                        </div>
                    </div>

                    {/* ETAP 2 */}
                    <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between group lg:mb-12">
                        <div className="w-full pl-12 lg:w-[45%] lg:pl-0 lg:pr-12 lg:text-right order-3 lg:order-1 mt-4 lg:mt-0">
                             <ul className="lg:hidden text-xs text-gray-400 space-y-1 bg-white/5 p-4 rounded-xl border border-white/5 inline-block w-full">
                                <li className="flex items-center gap-2">🔹 Telefon (Cold Call)</li>
                                <li className="flex items-center gap-2">🔹 Cold Email</li>
                                <li className="flex items-center gap-2">🔹 Wiadomość prywatna</li>
                             </ul>
                             <ul className="hidden lg:inline-block w-full text-xs text-gray-400 space-y-1 bg-white/5 p-4 rounded-xl border border-white/5">
                                <li className="flex items-center gap-2 justify-end">Telefon (Cold Call) 🔹</li>
                                <li className="flex items-center gap-2 justify-end">Cold Email 🔹</li>
                                <li className="flex items-center gap-2 justify-end">Wiadomość prywatna 🔹</li>
                             </ul>
                        </div>
                        <div className="absolute left-4 lg:left-1/2 w-8 h-8 lg:w-10 lg:h-10 -translate-x-1/2 bg-[#0b0c0e] border-4 border-purple-500 rounded-full flex items-center justify-center z-10 shadow-sm order-1">
                            <span className="text-[10px] lg:text-xs font-bold text-purple-500">2</span>
                        </div>
                        <div className="w-full pl-12 lg:w-[45%] lg:pl-12 order-2 lg:order-3 mt-2 lg:mt-0">
                            <span className="inline-block px-2 py-1 bg-purple-500/10 text-purple-400 text-[10px] font-bold uppercase tracking-wider rounded mb-2">Etap 02</span>
                            <h3 className="text-xl font-bold text-white mb-2">Direct Contact</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">Wchodzę do gry. Dzwonię, piszę, zagaduję. Wykorzystuję moją "gadkę", żeby przykuć uwagę i nie dać się zbyć.</p>
                        </div>
                    </div>

                    {/* ETAP 3 */}
                    <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between group lg:mb-12">
                        <div className="w-full pl-12 lg:w-[45%] lg:pl-0 lg:pr-12 lg:text-right order-2 lg:order-1 mt-2 lg:mt-0">
                            <span className="inline-block px-2 py-1 bg-orange-500/10 text-orange-400 text-[10px] font-bold uppercase tracking-wider rounded mb-2">Etap 03</span>
                            <h3 className="text-xl font-bold text-white mb-2">Networking & Relacja</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">Jeśli zimny kontakt to za mało, uruchamiam network. Szukam wspólnych znajomych i poleceń. Buduję most zaufania.</p>
                        </div>
                        <div className="absolute left-4 lg:left-1/2 w-8 h-8 lg:w-10 lg:h-10 -translate-x-1/2 bg-[#0b0c0e] border-4 border-orange-500 rounded-full flex items-center justify-center z-10 shadow-sm order-1">
                            <span className="text-[10px] lg:text-xs font-bold text-orange-500">3</span>
                        </div>
                        <div className="w-full pl-12 lg:w-[45%] lg:pl-12 order-3 mt-4 lg:mt-0">
                             <ul className="text-xs text-gray-400 space-y-1 bg-white/5 p-4 rounded-xl border border-white/5 inline-block w-full">
                                <li className="flex items-center gap-2">🔹 Uruchomienie Networku</li>
                                <li className="flex items-center gap-2">🔹 Budowanie zaufania</li>
                                <li className="flex items-center gap-2">🔹 Follow-up</li>
                             </ul>
                        </div>
                    </div>

                    {/* ETAP 4 */}
                    <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between group">
                        <div className="w-full pl-12 lg:w-[45%] lg:pl-0 lg:pr-12 lg:text-right order-3 lg:order-1 mt-4 lg:mt-0">
                             <ul className="lg:hidden text-xs text-gray-400 space-y-1 bg-white/5 p-4 rounded-xl border border-white/5 inline-block w-full">
                                <li className="flex items-center gap-2">🔹 Umówione spotkanie</li>
                                <li className="flex items-center gap-2">🔹 Gotowy klient</li>
                                <li className="flex items-center gap-2">🔹 Feedback</li>
                             </ul>
                             <ul className="hidden lg:inline-block w-full text-xs text-gray-400 space-y-1 bg-white/5 p-4 rounded-xl border border-white/5">
                                <li className="flex items-center gap-2 justify-end">Umówione spotkanie 🔹</li>
                                <li className="flex items-center gap-2 justify-end">Gotowy klient 🔹</li>
                                <li className="flex items-center gap-2 justify-end">Feedback 🔹</li>
                             </ul>
                        </div>
                        <div className="absolute left-4 lg:left-1/2 w-8 h-8 lg:w-10 lg:h-10 -translate-x-1/2 bg-[#0b0c0e] border-4 border-green-500 rounded-full flex items-center justify-center z-10 shadow-sm order-1">
                            <span className="text-[10px] lg:text-xs font-bold text-green-500">4</span>
                        </div>
                        <div className="w-full pl-12 lg:w-[45%] lg:pl-12 order-2 lg:order-3 mt-2 lg:mt-0">
                            <span className="inline-block px-2 py-1 bg-green-500/10 text-green-400 text-[10px] font-bold uppercase tracking-wider rounded mb-2">Etap 04</span>
                            <h3 className="text-xl font-bold text-white mb-2">Wynik (Spotkanie)</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">Dostajesz gotowego, "rozgrzanego" klienta na tacy. Twoim zadaniem jest tylko domknąć sprzedaż.</p>
                        </div>
                    </div>

                </div>
            </div>
        </div>
      </section>

      {/* --- FAQ (ACCORDION) --- */}
      <section className="py-24 px-6 bg-white border-t border-gray-100 relative">
        <GridPattern />
        <div className="mx-auto max-w-3xl relative z-10">
            <div className="text-left lg:text-center mb-16">
                <span className="text-blue-600 font-bold tracking-widest uppercase text-xs mb-3 block">Wiedza</span>
                <h2 className="text-3xl font-bold text-gray-900">Najczęstsze pytania</h2>
            </div>
            
            <div className="space-y-4">
                {faqData.map((item, index) => (
                    <FaqItem 
                        key={index} 
                        question={item.q} 
                        answer={item.a} 
                        isOpen={openFaqIndex === index}
                        onClick={() => toggleFaq(index)}
                    />
                ))}
            </div>
        </div>
      </section>

      {/* --- CTA --- */}
      <section className="py-32 px-6 relative overflow-hidden">
        <GridPattern />
        <div className="mx-auto max-w-4xl text-center relative z-10">
            <div className="inline-block p-4 rounded-full bg-blue-100 text-blue-600 mb-6">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
            </div>
            <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">Wypełnij swój lejek.</h2>
            <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
                Skup się na rozmowach z klientami, a nam zostaw poszukiwania i umawianie spotkań.
            </p>
            <Link href="/kontakt" className="inline-block rounded-full bg-black px-10 py-5 text-lg font-bold text-white shadow-xl hover:scale-105 hover:bg-gray-800 transition-all">
                Zamów darmową konsultację
            </Link>
        </div>
      </section>

    </main>
  );
}